import { useEffect, useRef } from 'react'

import type { DeliverySettlement } from '@/lib/delivery-settlement'
import type { PaymentEntryPoint } from '@/lib/payment-collection-tracking'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { sendOnceTracked } from '@/lib/payment-collection-tracking'

export type SettlementEntryPoint = Extract<
    PaymentEntryPoint,
    'dashboard' | 'trip' | 'order_detail'
>

/**
 * `delivery_settlement`: one attempt per opening of the "Xác nhận giao hàng"
 * popup. The terminal is the confirm-delivery API result, which arrives after
 * the popup has already closed, so the attempt lives with the always-mounted
 * dialog shell instead of the form. A failed confirm keeps the attempt open:
 * reopening the popup to retry is the same piece of work.
 */
export function useDeliverySettlementTracking({
    open,
    entryPoint,
    feeCollectible,
}: {
    open: boolean
    entryPoint: SettlementEntryPoint
    feeCollectible: boolean
}) {
    const workflow = useWorkflowTracking('delivery_settlement', {
        autoStart: false,
        operation: 'confirm',
    })
    const wasOpen = useRef(false)
    const submitted = useRef(false)

    useEffect(() => {
        if (open && !wasOpen.current) {
            submitted.current = false
            workflow.setContext({ entryPoint, feeCollectible })
            workflow.start()
        } else if (!open && wasOpen.current && !submitted.current) {
            workflow.finish('closed')
        }
        wasOpen.current = open
    }, [open, workflow, entryPoint, feeCollectible])

    return {
        /** Picking a method; the first pick marks when the driver decided. */
        choose(method: DeliverySettlement['method']) {
            workflow.interact('payment')
            workflow.setContext({ paymentMethod: method })
        },
        interact() {
            workflow.interact('payment')
        },
        invalid() {
            workflow.step('validation_failed')
        },
        /** Runs `send` exactly once; a rejection is recorded as `api_error`. */
        confirm(
            settlement: DeliverySettlement,
            due: number,
            send: () => unknown,
        ) {
            submitted.current = true
            sendOnceTracked((execute) => {
                workflow.setContext({
                    paymentMethod: settlement.method,
                    // Cash or a transfer into the driver's account, under the due.
                    cashPartial:
                        settlement.cash_amount !== undefined &&
                        settlement.cash_amount < due,
                    ...(feeCollectible
                        ? { collectFee: settlement.collect_shipping_fee }
                        : {}),
                })
                return workflow.run(execute)
            }, send)
        },
    }
}
